import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const EmployeeView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documentUrls, setDocumentUrls] = useState({
    aadharCard: null,
    panCard: null,
    educationCertificates: []
  });
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  useEffect(() => {
    if (!id) {
      toast.error('Employee identifier is missing.');
      navigate('/employee');
      return;
    }
    fetchEmployee(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchEmployee = async (employeeId) => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        action: 'get-employee',
        id: employeeId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;
      if (result?.success && result?.employee) {
        setEmployee(result.employee);
        // Fetch presigned URLs for documents if they exist
        if (result.employee.aadharCard || result.employee.panCard || (result.employee.educationCertificates && result.employee.educationCertificates.length > 0)) {
          fetchDocumentUrls(result.employee.id);
        }
      } else {
        toast.error(result?.message || 'Failed to fetch employee details');
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentUrls = async (employeeId) => {
    if (!API_URL || !employeeId) {
      return;
    }

    setLoadingDocuments(true);
    try {
      const payload = {
        action: 'get-employee-document-view-url',
        employeeId: employeeId,
        expiresIn: 7200 // 2 hours
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;
      if (result?.success && result?.files) {
        // Map files by documentType
        const urls = {
          aadharCard: null,
          panCard: null,
          educationCertificates: []
        };

        result.files.forEach((file) => {
          if (file.documentType === 'aadharCard') {
            urls.aadharCard = file.downloadUrl;
          } else if (file.documentType === 'panCard') {
            urls.panCard = file.downloadUrl;
          } else if (file.documentType === 'educationCertificate') {
            urls.educationCertificates.push(file.downloadUrl);
          }
        });

        setDocumentUrls(urls);
      } else {
        console.error('Failed to fetch document URLs:', result?.message);
        // Don't show error toast as documents are optional
      }
    } catch (error) {
      console.error('Error fetching document URLs:', error);
      // Don't show error toast as documents are optional
    } finally {
      setLoadingDocuments(false);
    }
  };

  const formatDate = (dateString, withTime = false) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (withTime) {
        return date.toLocaleString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const renderDetailItem = (label, value) => (
    <div className="employee-detail-item" key={label}>
      <span className="employee-detail-label">{label}</span>
      <span className="employee-detail-value">{value ?? '-'}</span>
    </div>
  );

  const employmentFlags = useMemo(() => {
    if (!employee) return [];
    return [
      { label: 'PF Applicable', value: employee.pfApplicable },
      { label: 'TDS Applicable', value: employee.tdsApplicable },
      { label: 'Remote Work Allowed', value: employee.remoteWorkAllowed },
      { label: 'Check-In/Out Required', value: !employee.checkInCheckOutNotRequired }
    ];
  }, [employee]);

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Employee Profile
                <i className="fas fa-user-check ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                Comprehensive view of the employee&apos;s personal, employment, and compliance details.
              </p>
            </div>
            <div className="col-md-4 employee-view-header-actions">
              <button type="button" className="employee-back-btn" onClick={() => navigate(-1)}>
                <i className="fas fa-arrow-left mr-2"></i>
                Back to Manage Employee
              </button>
            </div>
          </div>

          <div className="card employee-content-card">
            <div className="card-body employee-view-body">
              {loading ? (
                <Loading size={180} message="Loading employee details..." />
              ) : !employee ? (
                <div className="employee-empty">Employee details are not available.</div>
              ) : (
                <>
                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">Basic Information</h3>
                    </div>
                    <div className="employee-detail-grid three-column">
                      {renderDetailItem('Employee ID', employee.employeeId)}
                      {renderDetailItem('Full Name', employee.name)}
                      {renderDetailItem('Email', employee.email)}
                      {renderDetailItem('Mobile Number', employee.mobileNumber ? `${employee.countryCode || ''} ${employee.mobileNumber}` : '-')}
                      {renderDetailItem('Gender', employee.gender ? employee.gender.toUpperCase() : '-')}
                      {renderDetailItem('Date of Birth', formatDate(employee.dateOfBirth))}
                      {renderDetailItem('Blood Group', employee.bloodGroup)}
                      {renderDetailItem('Citizenship', employee.citizenship)}
                      {renderDetailItem('Marital Status', employee.maritalStatus)}
                      {renderDetailItem('Aadhar Number', employee.aadharNumber)}
                      {renderDetailItem('PAN Number', employee.panNumber)}
                    </div>
                  </section>

                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">Employment Details</h3>
                    </div>
                    <div className="employee-detail-grid three-column">
                      {renderDetailItem('Designation', employee.designationName || employee.designation || '-')}
                      {renderDetailItem('Department', employee.departmentName || employee.department || '-')}
                      {renderDetailItem('Reporting Manager', employee.reportingManagerName || employee.reportingManager || '-')}
                      {renderDetailItem('Employment Type', employee.employmentType)}
                      {renderDetailItem('Date of Joining', formatDate(employee.dateOfJoining))}
                      {renderDetailItem('PF UAN Number', employee.pfUanNumber)}
                      {renderDetailItem('Employment Status', employee.employmentStatus?.toUpperCase())}
                    </div>
                    <div className="employee-flags">
                      {employmentFlags.map((flag) => (
                        <span key={flag.label} className={`employee-flag ${flag.value ? 'active' : ''}`}>
                          <i className={`fas ${flag.value ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                          {flag.label}: {flag.value ? 'Yes' : 'No'}
                        </span>
                      ))}
                    </div>
                  </section>

                  {employee.addresses?.length > 0 && (
                    <section className="employee-section">
                      <div className="employee-section-header">
                        <h3 className="employee-section-title">Addresses</h3>
                      </div>
                      <div className="employee-address-grid">
                        {employee.addresses.map((address, index) => (
                          <div className="employee-address-card" key={`${address.addressType}-${index}`}>
                            <div className="employee-address-type">{address.addressType?.toUpperCase()} ADDRESS</div>
                            <div className="employee-address-line">{address.address1}</div>
                            {address.address2 && <div className="employee-address-line">{address.address2}</div>}
                            <div className="employee-address-line">
                              {[address.city, address.state, address.country].filter(Boolean).join(', ')}
                            </div>
                            <div className="employee-address-line">PIN: {address.pincode || '-'}</div>
                            {address.updatedAt && (
                              <div className="employee-address-updated">Updated on {formatDate(address.updatedAt, true)}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {employee.experiences?.length > 0 && (
                    <section className="employee-section">
                      <div className="employee-section-header">
                        <h3 className="employee-section-title">Experience History</h3>
                      </div>
                      <div className="employee-timeline">
                        {employee.experiences.map((experience, index) => (
                          <div className="employee-timeline-item" key={`${experience.companyName}-${index}`}>
                            <div className="employee-timeline-header">
                              <div>
                                <h4>{experience.companyName}</h4>
                                <p>{experience.designation}</p>
                              </div>
                              <span className="employee-duration">{experience.duration}</span>
                            </div>
                            <div className="employee-timeline-dates">
                              {formatDate(experience.periodFrom)} - {experience.isPresent ? 'Present' : formatDate(experience.periodTo)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {employee.educationDetails?.length > 0 && (
                    <section className="employee-section">
                      <div className="employee-section-header">
                        <h3 className="employee-section-title">Education</h3>
                      </div>
                      <div className="employee-education-grid">
                        {employee.educationDetails.map((education, index) => (
                          <div className="employee-education-card" key={`${education.qualification}-${index}`}>
                            <h4>{education.qualification}</h4>
                            <p>{education.boardUniversity}</p>
                            <div className="employee-education-meta">
                              <span>Year: {education.yearOfPassing}</span>
                              <span>Score: {education.percentage}%</span>
                            </div>
                            {education.nameOfDegree && education.nameOfDegree.trim() && (
                              <div className="employee-education-meta">
                                <span>Degree: {education.nameOfDegree}</span>
                              </div>
                            )}
                            {education.fieldOfStudy && education.fieldOfStudy.trim() && (
                              <div className="employee-education-meta">
                                <span>Field of Study: {education.fieldOfStudy}</span>
                              </div>
                            )}
                            {education.relevantCertificateNo && education.relevantCertificateNo.trim() && (
                              <div className="employee-education-meta">
                                <span>Certificate No: {education.relevantCertificateNo}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">Bank Details</h3>
                    </div>
                    <div className="employee-detail-grid two-column">
                      {renderDetailItem('Bank Name', employee.bankName)}
                      {renderDetailItem('Account Holder', employee.accountHolderName)}
                      {renderDetailItem('Account Number', employee.accountNumber)}
                      {renderDetailItem('IFSC Code', employee.ifscCode)}
                      {renderDetailItem('Branch Name', employee.branchName)}
                    </div>
                  </section>

                  {(employee.aadharCard || employee.panCard || (employee.educationCertificates && employee.educationCertificates.length > 0)) && (
                    <section className="employee-section">
                      <div className="employee-section-header">
                        <h3 className="employee-section-title">Documents</h3>
                        {loadingDocuments && (
                          <small className="text-muted">
                            <i className="fas fa-spinner fa-spin mr-1"></i>
                            Loading document access...
                          </small>
                        )}
                      </div>
                      <div className="employee-documents-grid">
                        {employee.aadharCard && (
                          <div className="employee-document-item">
                            <div className="employee-document-label">Aadhar Card</div>
                            <div className="employee-document-preview">
                              {loadingDocuments ? (
                                <div className="employee-document-link" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Loading...
                                </div>
                              ) : documentUrls.aadharCard ? (
                                <a 
                                  href={documentUrls.aadharCard} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="employee-document-link"
                                >
                                  <i className="fas fa-file-pdf mr-2"></i>
                                  View Aadhar Card
                                </a>
                              ) : (
                                <div className="employee-document-link" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                                  <i className="fas fa-exclamation-circle mr-2"></i>
                                  Unable to load
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {employee.panCard && (
                          <div className="employee-document-item">
                            <div className="employee-document-label">PAN Card</div>
                            <div className="employee-document-preview">
                              {loadingDocuments ? (
                                <div className="employee-document-link" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Loading...
                                </div>
                              ) : documentUrls.panCard ? (
                                <a 
                                  href={documentUrls.panCard} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="employee-document-link"
                                >
                                  <i className="fas fa-file-pdf mr-2"></i>
                                  View PAN Card
                                </a>
                              ) : (
                                <div className="employee-document-link" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                                  <i className="fas fa-exclamation-circle mr-2"></i>
                                  Unable to load
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {employee.educationCertificates && employee.educationCertificates.length > 0 && (
                          <div className="employee-document-item">
                            <div className="employee-document-label">Education Certificates</div>
                            <div className="employee-document-list">
                              {loadingDocuments ? (
                                <div className="employee-document-preview">
                                  <div className="employee-document-link" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                    Loading...
                                  </div>
                                </div>
                              ) : documentUrls.educationCertificates.length > 0 ? (
                                documentUrls.educationCertificates.map((certificateUrl, index) => (
                                  <div key={index} className="employee-document-preview">
                                    <a 
                                      href={certificateUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="employee-document-link"
                                    >
                                      <i className="fas fa-file-pdf mr-2"></i>
                                      View Certificate {index + 1}
                                    </a>
                                  </div>
                                ))
                              ) : (
                                <div className="employee-document-preview">
                                  <div className="employee-document-link" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                                    <i className="fas fa-exclamation-circle mr-2"></i>
                                    Unable to load certificates
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">Audit Trail</h3>
                    </div>
                    <div className="employee-detail-grid two-column">
                      {renderDetailItem('Created On', formatDate(employee.createdAt, true))}
                      {renderDetailItem('Last Updated', formatDate(employee.updatedAt, true))}
                      {renderDetailItem('Profile Status', employee.status?.toUpperCase())}
                    </div>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmployeeView;